import React from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import EditorFormik from '~/components/communications/EmailCommunicationCard/EmailEditor/EditorFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import TextAreaWithTokens from '~/components/core/Formik/TextAreaWithTokens';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type {
  PhoneProfileInfoPhoneProfileVoiceMessageRef as VoiceMessageConfiguration,
  VoiceMessageConfigurationParams,
} from '~/server_shared/openapi/data-contracts';

import VoiceFileUploaderFormik from './VoiceFileUploaderFormik';

interface TokenInfo {
  desc: string;
}

interface VoiceMessageConfigurationDialogProps {
  title?: string | React.ReactNode;
  voiceMessageConfiguration?: VoiceMessageConfigurationParams | VoiceMessageConfiguration | null;
  allowedTokens?: Record<string, TokenInfo>;
  textAreaLabel?: string;
  textAreaId: string;
  voiceFileInputId: string;
  radioButtonGroupId: string;
  onReplaceExistingRecording: () => void;
  storedFileIdPath?: string;
}

const VoiceMessageConfigurationDialog: React.FC<VoiceMessageConfigurationDialogProps> = ({
  title,
  voiceMessageConfiguration,
  allowedTokens,
  textAreaLabel,
  textAreaId,
  voiceFileInputId,
  radioButtonGroupId,
  onReplaceExistingRecording,
  storedFileIdPath,
}) => {
  const { values, setErrors } = useFormikContext();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  return (
    <div className="mt-16">
      {title ? (
        <Typography variant="subtitle1" className="mb-5">
          {title}
        </Typography>
      ) : null}
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioButtonGroupFormik
              id={radioButtonGroupId}
              direction="row"
              options={[
                { text: 'Add text message', optionValue: 'text_message' },
                {
                  text: 'Upload voice message',
                  optionValue: 'uploaded_text_message',
                },
              ]}
              variant="subtitle1"
              onClick={() => {
                setErrors({});
              }}
            />
          </Grid>
        </Grid>
        <div className="mt-12">
          {get(values, radioButtonGroupId) === 'text_message' ? (
            allowedTokens ? (
              <TextAreaWithTokens
                charsLimit={undefined}
                id={textAreaId}
                label={textAreaLabel || ''}
                onAddToken={undefined}
                onChooseToken={undefined}
                tokensList={allowedTokens}
              />
            ) : (
              <EditorFormik
                id={textAreaId}
                label={textAreaLabel}
                classNameEditor="min-h-[100px] overflow-auto max-h-[300px]"
                sanitizeContent
              />
            )
          ) : null}

          {get(values, radioButtonGroupId) === 'uploaded_text_message' ? (
            <VoiceFileUploaderFormik
              voiceFileInputId={voiceFileInputId}
              storedFileIdPath={storedFileIdPath}
              existingVoiceFileUrl={voiceMessageConfiguration?.permanent_audio_file_url || undefined}
              onReplaceExistingRecording={onReplaceExistingRecording}
              urlPrefix={`/api/v1/organizations/${organization.id}/stored_files`}
              useStoredFileIdPathAsErrorField={!!storedFileIdPath}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VoiceMessageConfigurationDialog;
